import DefaultButton from '@/components/common/default/DefaultButton';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import { Transition } from '@headlessui/react';
import { ChevronRight, Settings } from 'lucide-react';
import { Fragment, useRef, useEffect } from 'react';
import clsx from 'clsx';
import DefaultLink from '@/components/common/default/DefaultLink';
import uiItems from '@/constants/ui-items/index.constants';
import useStaticNotifications from '@/hooks/useStaticNotifications';
import HeaderCtaCount from './HeaderCtaCount';

export default function HeaderUserDropdownSettings({
  settingsDropdownOpen,
  setSettingsDropdownOpen,
}: {
  settingsDropdownOpen: boolean;
  setSettingsDropdownOpen: (open: boolean) => void;
}) {
  const { sellerProfileNoti, buyerProfileNoti, totalNoti } = useStaticNotifications();
  const { translate } = useTypedTranslation<HeaderEnum>('header');

  // Ref for close timer
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Ref for dropdown to detect hover
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Function to start close timer
  const startCloseTimer = () => {
    // Clear any existing timer first
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    // Set new timer
    closeTimerRef.current = setTimeout(() => {
      setSettingsDropdownOpen(false);
    }, 6000);
  };

  // Function to cancel timer
  const cancelCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Close on unmount
  useEffect(() => {
    return () => {
      setSettingsDropdownOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to handle timer cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Effect to start/cancel timer when dropdown state changes
  useEffect(() => {
    if (settingsDropdownOpen) {
      startCloseTimer();
    } else {
      cancelCloseTimer();
    }
  }, [settingsDropdownOpen]);

  return (
    <nav className="tw-relative tw-w-full" aria-label="Settings Menu">
      <DefaultButton
        onClick={() => {
          setSettingsDropdownOpen(!settingsDropdownOpen);
          if (!settingsDropdownOpen) {
            // If opening dropdown, start timer
            startCloseTimer();
          } else {
            // If closing dropdown, cancel timer
            cancelCloseTimer();
          }
        }}
        aria-haspopup="menu"
        aria-expanded={settingsDropdownOpen}
        aria-controls="settings-dropdown"
        className="tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-whitespace-nowrap tw-rounded-md tw-py-2 tw-pl-4 tw-pr-4 tw-font-normal tw-text-charcoal-blue tw-transition-colors tw-duration-200 hover:tw-bg-gray-100 nine:tw-max-w-[unset]"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <Settings className={'tw-h-5 tw-w-5 tw-stroke-charcoal-blue'} />
          {translate(HeaderEnum['dashboard_menu.main.settings'])}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <HeaderCtaCount cta={totalNoti ?? 0} />
          <ChevronRight className="tw-h-5 tw-w-5 tw-stroke-charcoal-blue" aria-hidden="true" />
        </div>
      </DefaultButton>

      <Transition
        show={settingsDropdownOpen}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0 tw-scale-95"
        enterTo="tw-opacity-100 tw-scale-100"
        leave="tw-transition tw-ease-in tw-duration-150"
        as={Fragment}
      >
        <div
          id="settings-dropdown"
          role="menu"
          aria-orientation="vertical"
          className="tw-absolute tw-top-0 tw-z-[60] tw-mt-2 tw-min-w-40 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-shadow-xl max-2xl:-tw-left-[230px] 2xl:-tw-right-[230px]"
          ref={dropdownRef}
          // Detect when mouse enters and leaves dropdown
          onMouseEnter={cancelCloseTimer}
          onMouseLeave={startCloseTimer}
          // Also detect touch interaction
          onTouchStart={cancelCloseTimer}
          tabIndex={0} // Make dropdown focusable
        >
          <ul
            className="w-fit tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0 tw-py-2"
            role="menu" // Changed from "none" to "menu" for better accessibility
          >
            {uiItems.headerV2.dashboardMenuSettings.map(option => (
              <li
                key={option.label}
                className="tw-flex tw-items-center tw-justify-start"
                role="none"
              >
                <DefaultLink
                  href={option.action}
                  role="menuitem"
                  tabIndex={settingsDropdownOpen ? 0 : -1} // Make links focusable only when menu is open
                  className={clsx(
                    'tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-whitespace-nowrap tw-rounded-md tw-py-2 tw-pl-4 tw-font-normal tw-text-charcoal-blue tw-transition-colors tw-duration-200 hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-main/30', // Added focus styles
                    {
                      'tw-pr-4':
                        option.label === HeaderEnum['dashboard_menu.settings_menu.your_account'] ||
                        option.label === HeaderEnum['dashboard_menu.settings_menu.buyer_profile'] ||
                        option.label === HeaderEnum['dashboard_menu.settings_menu.seller_profile'],
                      'tw-pr-24':
                        option.label !== HeaderEnum['dashboard_menu.settings_menu.your_account'] &&
                        option.label !== HeaderEnum['dashboard_menu.settings_menu.buyer_profile'] &&
                        option.label !== HeaderEnum['dashboard_menu.settings_menu.seller_profile'],
                    }
                  )}
                >
                  <div className="tw-flex tw-items-center tw-justify-start tw-gap-3">
                    <option.extraIcon className="tw-h-5 tw-w-5 tw-stroke-charcoal-blue" />
                    {translate(option.label)}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {option.label === HeaderEnum['dashboard_menu.settings_menu.buyer_profile'] && (
                      <HeaderCtaCount cta={buyerProfileNoti ?? 0} />
                    )}
                    {option.label === HeaderEnum['dashboard_menu.settings_menu.seller_profile'] && (
                      <HeaderCtaCount cta={sellerProfileNoti ?? 0} />
                    )}
                    {option.label === HeaderEnum['dashboard_menu.settings_menu.your_account'] && (
                      <HeaderCtaCount cta={totalNoti ?? 0} />
                    )}
                  </div>
                </DefaultLink>
              </li>
            ))}
          </ul>
        </div>
      </Transition>
    </nav>
  );
}
